/**
 * Preference Writer — Serialized Single-Flight with Per-Key Coalescing
 *
 * Batches preference writes into a single GraphQL mutation per key.
 * - pending: Map<key, value> — last-wins for same key, insertion-order FIFO for distinct keys
 * - running: boolean guard — prevents parallel drains
 * - retryTimer: number | null — setTimeout ID for 1s retry on failure
 *
 * Enqueue behavior:
 *   - Map.set(key, value) — overwrites if key already pending
 *   - Schedules drain via queueMicrotask (or Promise.resolve().then fallback)
 *   - Returns immediately (non-blocking)
 *
 * Drain behavior:
 *   - Loops while pending.size > 0
 *   - Takes first entry in insertion order (pending.entries().next().value)
 *   - Awaits savePreference(key, JSON.stringify(value))
 *   - On failure: re-queues only if !pending.has(key) (do not clobber newer value)
 *   - Schedules 1s retry via setTimeout
 *   - Never parallel (running guard)
 *
 * JSON shapes (stored as strings on backend):
 *   - selectedAgencyId: string (e.g., "agency-123" or "")
 *   - ordersView: string (e.g., "orders-map" or "orders-list")
 *   - orderColumns: array of strings (e.g., ["order_id", "agency", ...])
 *   - recentOrders: array of objects (e.g., [{ order_id: "...", lastSeen: 1234567890 }, ...])
 *   - orderMenuCollapsed: boolean (e.g., true or false)
 *   - assistantFullscreen: boolean (e.g., true or false)
 *   - language: string ("en" or "es")
 */

/**
 * Create a preference writer instance.
 *
 * @param {Object} options
 * @param {Function} options.savePreference - async (key: string, value: string) => Promise
 *   Called with key and JSON.stringify(value). Must handle GraphQL errors.
 * @returns {Object} writer instance with enqueue() and flush() methods
 */
export const createPreferenceWriter = ({ savePreference }) => {
  const pending = new Map();
  let running = false;
  let retryTimer = null;

  /**
   * Drain pending writes in insertion order, one at a time.
   * Never parallel (running guard).
   */
  const drain = async () => {
    if (running) {
      return;
    }

    running = true;

    try {
      while (pending.size > 0) {
        // Take first entry in insertion order
        const [key, value] = pending.entries().next().value;
        pending.delete(key);

        try {
          // Call savePreference with JSON.stringify(value)
          await savePreference(key, JSON.stringify(value));
        } catch (err) {
          // On failure: re-queue only if !pending.has(key)
          // (do not clobber a newer value queued while request was in-flight)
          if (!pending.has(key)) {
            pending.set(key, value);
          }

          // Schedule 1s retry
          if (retryTimer !== null) {
            clearTimeout(retryTimer);
          }
          retryTimer = setTimeout(drain, 1000);

          // Stop draining; retry will continue
          break;
        }
      }
    } finally {
      running = false;
    }
  };

  /**
   * Schedule drain via queueMicrotask, with Promise.resolve().then fallback.
   */
  const scheduleDrain = () => {
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(drain);
    } else {
      Promise.resolve().then(drain);
    }
  };

  /**
   * Enqueue a preference write.
   * Last-wins for same key; insertion-order FIFO for distinct keys.
   *
   * @param {string} key - preference key
   * @param {*} value - preference value (will be JSON.stringify'd)
   */
  const enqueue = (key, value) => {
    pending.set(key, value);
    scheduleDrain();
  };

  /**
   * Flush pending writes (for testing).
   * Waits for drain to complete.
   */
  const flush = async () => {
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    await drain();
  };

  return {
    enqueue,
    flush,
  };
};

export default { createPreferenceWriter };
